import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore, type AppRole } from '@/stores/auth';
import { useUiModeStore } from '@/stores/uiMode';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    roles?: AppRole[];
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/Home.vue') },
  {
    // Simple mode (2026-07): a task-first, plain-language workspace for the
    // account operator (Profile Admin). The full app remains at its own routes.
    path: '/simple',
    name: 'simple-home',
    component: () => import('@/views/simple/SimpleHome.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    path: '/simple/add',
    name: 'simple-add',
    component: () => import('@/views/simple/AddPerson.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    path: '/simple/people',
    name: 'simple-people',
    component: () => import('@/views/simple/People.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    path: '/simple/people/:id',
    name: 'simple-person',
    component: () => import('@/views/simple/PersonDetail.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    // Plain-language help for Simple mode. Same gate as the rest of Simple:
    // it describes those screens and nothing else.
    path: '/simple/help',
    name: 'simple-help',
    component: () => import('@/views/simple/SimpleHelp.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    // Manager self-service (spec §4.6). Requires sign-in but NO app role – the
    // backend authorizes each account on the stamped manager relationship.
    path: '/my-team',
    name: 'my-team',
    component: () => import('@/views/MyTeam.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/users/new',
    name: 'user-new',
    component: () => import('@/views/UserNew.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    path: '/users/bulk',
    name: 'users-bulk',
    component: () => import('@/views/UsersBulk.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin'] },
  },
  {
    path: '/users/:id',
    name: 'user-detail',
    component: () => import('@/views/UserDetail.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/groups',
    name: 'groups',
    component: () => import('@/views/Groups.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/groups/:id',
    name: 'group-detail',
    component: () => import('@/views/GroupDetail.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/profiles',
    name: 'profiles',
    component: () => import('@/views/Profiles.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/profiles/new',
    name: 'profile-new',
    component: () => import('@/views/ProfileEditor.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileManager'] },
  },
  {
    path: '/profiles/:id',
    name: 'profile-edit',
    component: () => import('@/views/ProfileEditor.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/views/Audit.vue'),
    meta: { requiresAuth: true, roles: ['Admin', 'ProfileAdmin', 'Auditor', 'HelpDesk'] },
  },
  {
    path: '/admin/config',
    name: 'admin-config',
    component: () => import('@/views/AdminConfig.vue'),
    meta: { requiresAuth: true, roles: ['Admin'] },
  },
  {
    path: '/admin/tag-rules',
    name: 'tag-rules',
    component: () => import('@/views/TagRules.vue'),
    meta: { requiresAuth: true, roles: ['Admin'] },
  },
  {
    // Not admin-only: every role-holder manages their own keys, and a key can
    // never exceed its owner's roles. The view widens to all keys for an Admin.
    path: '/api-keys',
    name: 'api-keys',
    component: () => import('@/views/ApiKeys.vue'),
    meta: {
      requiresAuth: true,
      roles: ['Admin', 'ProfileManager', 'ProfileAdmin', 'Auditor', 'HelpDesk'],
    },
  },
  {
    path: '/docs',
    name: 'docs',
    component: () => import('@/views/Docs.vue'),
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/Forbidden.vue'),
  },
  {
    // Catch-all: a stale bookmark, a typo, or a click on a non-route path lands
    // here instead of rendering a blank page inside the shell.
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();

  // Operators default into Simple mode: the home route lands them on the
  // task-first workspace rather than the full dashboard. Only once signed in,
  // so the sign-in card still shows first.
  if (to.name === 'home' && auth.signedIn && useUiModeStore().isSimple) {
    return { name: 'simple-home' };
  }

  if (!to.meta.requiresAuth) return true;

  if (!auth.signedIn) {
    // App.vue renders a sign-in card whenever signedIn is false, so we don't
    // need to call auth.signIn() from here – the user picks Sign in with a
    // real click. Calling it from the guard previously meant any race that
    // hit a not-yet-initialised MSAL produced an uncaught-promise rejection
    // in the console. Send them to home; App.vue's sign-in overlay covers it.
    return { name: 'home', query: { from: to.fullPath } };
  }
  if (to.meta.roles && !auth.hasRole(...to.meta.roles)) {
    return { name: 'forbidden', query: { from: to.fullPath } };
  }
  return true;
});
